import type { Article } from '@services/knowledge.service';
import type { EmbeddingStatus } from '@services/knowledge-analytics.service';
import { isStale, type ArticleRow, type ArticleStatus } from './types';

// The service's Article → the register row (artboard 16a).
//
// Every field comes off the list payload as it already arrives. Nothing here
// needs a migration, and nothing is invented — in particular, helpfulness is
// NOT derived from views. Views are reads, not approval; conflating them is the
// one change on this screen that would actively mislead.

const STATUSES = new Set<ArticleStatus>(['published', 'draft', 'archived']);

/**
 * Is this article in the search index?
 *
 * Two sources, in priority order: the embedding-status report when it has a row
 * (authoritative), then the article's own columns. Deliberately a BOOLEAN and
 * nothing more — a stale row collapses to indexed here, because "indexed, then
 * edited" is a third state the column no longer claims to distinguish. It comes
 * back as the three-state `USED BY THE AI` when `embedded_at` and
 * `content_updated_at` both exist (phase 27 §11).
 *
 * The report's field is `embeddingStatus`, not `status`. Reading the wrong
 * name compared `undefined` against a string on every call, so every article
 * that HAD a report row — which is every published article — reported as not
 * indexed.
 */
export function isIndexed(article: Article, report?: EmbeddingStatus): boolean {
  if (report) return report.embeddingStatus === 'completed';
  if (article.embeddingStatus) return article.embeddingStatus === 'completed';
  // No signal either way — chunks in the index are the fact that matters.
  return (article.chunkCount ?? 0) > 0;
}

export function toArticleRow(
  a: Article,
  reports?: Map<string, EmbeddingStatus>,
): ArticleRow {
  const status = String(a.status ?? '').toLowerCase();
  return {
    id: a.id,
    title: a.title,
    slug: a.slug,
    category: a.category?.name ?? null,
    author: a.authorName ?? null,
    status: STATUSES.has(status as ArticleStatus) ? (status as ArticleStatus) : 'draft',
    views: a.viewCount ?? 0,
    updatedAt: a.updatedAt,
    isIndexed: isIndexed(a, reports?.get(a.id)),
  };
}

/** Articles nobody has reviewed inside the threshold — what the band counts. */
export function staleArticles(rows: ArticleRow[]): ArticleRow[] {
  return rows.filter((r) => isStale(r.updatedAt));
}
